import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  BookOpen, Download, ChevronDown, ChevronRight,
  Camera, MapPin, Shield, Users, Building2, FileText,
  Smartphone, CalendarDays, ScanFace, Clock, Wifi, WifiOff,
  AlertCircle, Settings, Image, User
} from 'lucide-react';
import jsPDF from 'jspdf';

const Section = ({ title, icon: Icon, children, defaultOpen = false, color = "indigo" }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="mb-4 border-l-4" style={{ borderLeftColor: color === 'amber' ? '#f59e0b' : color === 'emerald' ? '#10b981' : color === 'blue' ? '#3b82f6' : color === 'red' ? '#ef4444' : '#4f46e5' }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        data-testid={`section-${title.toLowerCase().replace(/\s/g, '-')}`}
      >
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-indigo-600" />}
          <h3 className="font-semibold text-gray-900 text-left">{title}</h3>
        </div>
        {open ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
      </button>
      {open && <CardContent className="pt-0 pb-4 px-4">{children}</CardContent>}
    </Card>
  );
};

const Step = ({ number, text }) => (
  <div className="flex gap-3 mb-3">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">{number}</div>
    <p className="text-gray-700 text-sm pt-0.5">{text}</p>
  </div>
);

const Tip = ({ children }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-3">
    <p className="text-sm text-amber-800"><span className="font-semibold">Tips:</span> {children}</p>
  </div>
);

// ===== GUIDE CONTENT =====

const guruGuide = [
  {
    title: "Cara Melakukan Absensi",
    icon: Camera,
    color: "indigo",
    defaultOpen: true,
    content: (
      <>
        <Step number="1" text="Buka menu Absensi dari sidebar." />
        <Step number="2" text="Pastikan GPS dan kamera aktif. Aplikasi akan meminta izin akses." />
        <Step number="3" text="Ambil foto selfie. Pastikan wajah terlihat jelas." />
        <Step number="4" text="Jika wajah sudah didaftarkan, verifikasi wajah otomatis dilakukan (kedipkan mata untuk liveness detection)." />
        <Step number="5" text="Tekan tombol 'Absen Masuk' atau 'Absen Pulang'." />
        <Step number="6" text="Foto akan diberi watermark otomatis (nama, waktu, koordinat, sekolah)." />
        <Tip>Jika GPS tidak akurat, pindah ke tempat terbuka. Akurasi GPS minimal 100m.</Tip>
        <Tip>Untuk sekolah dengan 2 shift, sistem otomatis menentukan shift berdasarkan waktu absen Anda.</Tip>
      </>
    )
  },
  {
    title: "Mode Offline",
    icon: WifiOff,
    color: "amber",
    content: (
      <>
        <p className="text-sm text-gray-700 mb-3">Aplikasi dapat digunakan tanpa internet:</p>
        <Step number="1" text="Lakukan absensi seperti biasa meskipun tidak ada koneksi internet." />
        <Step number="2" text="Data disimpan di perangkat dan ditandai sebagai 'pending'." />
        <Step number="3" text="Saat internet kembali tersedia, data otomatis disinkronkan." />
        <Step number="4" text="Anda juga dapat menekan tombol 'Sync' manual di sidebar." />
        <Tip>Verifikasi wajah tetap bisa dilakukan offline menggunakan data wajah yang sudah tersimpan di perangkat.</Tip>
      </>
    )
  },
  {
    title: "Upload Foto Kegiatan",
    icon: Image,
    color: "emerald",
    content: (
      <>
        <Step number="1" text="Buka menu 'Foto Kegiatan' dari sidebar." />
        <Step number="2" text="Anda harus sudah melakukan absensi masuk hari ini." />
        <Step number="3" text="Ambil foto atau pilih dari galeri, beri keterangan." />
        <Step number="4" text="Maksimal 3 foto kegiatan per hari." />
      </>
    )
  },
  {
    title: "Mengajukan Izin",
    icon: AlertCircle,
    color: "blue",
    content: (
      <>
        <Step number="1" text="Buka menu 'Izin' dari sidebar." />
        <Step number="2" text="Klik tombol 'Ajukan Izin'." />
        <Step number="3" text="Isi tanggal mulai, tanggal selesai, dan alasan izin." />
        <Step number="4" text="Lampirkan surat jika diperlukan." />
        <Step number="5" text="Izin akan menunggu persetujuan Kepala Sekolah atau Admin." />
      </>
    )
  },
  {
    title: "Mendaftarkan Wajah",
    icon: ScanFace,
    color: "indigo",
    content: (
      <>
        <Step number="1" text="Buka menu 'Profil' dari sidebar." />
        <Step number="2" text="Klik 'Daftarkan Wajah'." />
        <Step number="3" text="Posisikan wajah di dalam bingkai kamera." />
        <Step number="4" text="Kedipkan mata untuk verifikasi liveness." />
        <Step number="5" text="Wajah berhasil didaftarkan dan akan digunakan untuk verifikasi absensi." />
        <Tip>Wajah juga bisa didaftarkan oleh Admin atau Kepala Sekolah melalui menu 'Daftar Wajah'.</Tip>
      </>
    )
  },
];

const kepsekGuide = [
  {
    title: "Monitoring Kehadiran Guru",
    icon: Users,
    color: "indigo",
    defaultOpen: true,
    content: (
      <>
        <Step number="1" text="Dashboard menampilkan ringkasan kehadiran guru di sekolah Anda hari ini." />
        <Step number="2" text="Lihat siapa saja yang sudah dan belum absen." />
        <Step number="3" text="Buka menu 'Riwayat' untuk melihat riwayat absensi guru per tanggal." />
        <Step number="4" text="Buka menu 'Laporan' untuk rekap bulanan dan skor kinerja guru." />
      </>
    )
  },
  {
    title: "Menyetujui/Menolak Izin",
    icon: AlertCircle,
    color: "amber",
    content: (
      <>
        <Step number="1" text="Buka menu 'Izin' dari sidebar." />
        <Step number="2" text="Daftar izin guru di sekolah Anda akan tampil." />
        <Step number="3" text="Klik 'Setujui' atau 'Tolak' pada izin yang pending." />
      </>
    )
  },
  {
    title: "Mendaftarkan Wajah Guru",
    icon: ScanFace,
    color: "emerald",
    content: (
      <>
        <Step number="1" text="Buka menu 'Daftar Wajah' dari sidebar." />
        <Step number="2" text="Pilih guru yang belum memiliki data wajah." />
        <Step number="3" text="Ambil foto wajah guru menggunakan kamera." />
        <Step number="4" text="Sistem akan menyimpan data biometrik wajah untuk verifikasi absensi." />
        <Tip>Fitur ini berguna jika guru kesulitan mendaftarkan wajah sendiri.</Tip>
      </>
    )
  },
  {
    title: "Mode Kiosk (Device Bersama)",
    icon: Smartphone,
    color: "blue",
    content: (
      <>
        <p className="text-sm text-gray-700 mb-3">Mode Kiosk memungkinkan satu perangkat digunakan banyak guru untuk absensi:</p>
        <Step number="1" text="Buka menu 'Mode Kiosk' dari sidebar." />
        <Step number="2" text="Klik 'Daftarkan Device' dan beri nama perangkat." />
        <Step number="3" text="Salin token device yang dihasilkan." />
        <Step number="4" text="Buka halaman Kiosk di perangkat bersama dan masukkan token." />
        <Step number="5" text="Guru pilih nama, verifikasi wajah, dan absen." />
      </>
    )
  },
];

const adminGuide = [
  {
    title: "Kelola Sekolah",
    icon: Building2,
    color: "indigo",
    defaultOpen: true,
    content: (
      <>
        <Step number="1" text="Buka menu 'Sekolah' dari sidebar." />
        <Step number="2" text="Klik 'Tambah Sekolah' untuk menambah sekolah baru." />
        <Step number="3" text="Isi nama, alamat, jenjang, koordinat GPS, radius geofence." />
        <Step number="4" text="Atur jam operasional: pilih 'Pagi Saja' atau 'Pagi & Siang (2 Shift)'." />
        <Step number="5" text="Untuk 2 shift, atur jam masuk/pulang shift siang dan toleransi." />
        <Step number="6" text="Klik sekolah untuk melihat detail dan daftar guru." />
        <Tip>Radius geofence menentukan area valid absensi. Default 100 meter.</Tip>
      </>
    )
  },
  {
    title: "Manajemen User",
    icon: Users,
    color: "emerald",
    content: (
      <>
        <Step number="1" text="Buka menu 'Manajemen User' dari sidebar." />
        <Step number="2" text="Tambah user baru dengan NIP, nama, role, dan sekolah." />
        <Step number="3" text="Role yang tersedia: Admin Dinas, Pimpinan, Editor, Pengawas, Kepala Sekolah, Guru." />
        <Step number="4" text="Edit atau hapus user yang sudah ada." />
        <Step number="5" text="Password harus minimal 8 karakter, mengandung huruf besar, huruf kecil, dan angka." />
      </>
    )
  },
  {
    title: "Pengaturan Timezone & Hari Aktif",
    icon: Settings,
    color: "amber",
    content: (
      <>
        <p className="text-sm text-gray-700 mb-3"><strong>Timezone:</strong></p>
        <Step number="1" text="Buka menu 'Pengaturan' dari sidebar." />
        <Step number="2" text="Pilih timezone: WIB (UTC+7), WITA (UTC+8), atau WIT (UTC+9)." />
        <Step number="3" text="Timezone mempengaruhi perhitungan status kehadiran dan watermark foto." />
        <p className="text-sm text-gray-700 mb-3 mt-4"><strong>Hari Aktif & Libur:</strong></p>
        <Step number="4" text="Buka menu 'Hari Aktif' dari sidebar." />
        <Step number="5" text="Centang hari-hari sekolah aktif (default: Senin-Jumat)." />
        <Step number="6" text="Tambah hari libur nasional atau lokal beserta keterangan." />
      </>
    )
  },
  {
    title: "Security Alerts & Monitoring",
    icon: Shield,
    color: "red",
    content: (
      <>
        <Step number="1" text="Buka menu 'Security Alerts' dari sidebar." />
        <Step number="2" text="Lihat daftar alert keamanan: fake GPS, di luar radius, anomali waktu." />
        <Step number="3" text="Review alert: tandai sebagai 'Reviewed', 'Confirmed', atau 'Dismissed'." />
        <Step number="4" text="Alert dengan severity 'Critical' atau 'High' perlu ditindaklanjuti segera." />
        <Tip>Sistem otomatis mendeteksi GPS palsu, absensi di luar jam kerja, dan anomali timestamp.</Tip>
      </>
    )
  },
  {
    title: "Laporan & Export",
    icon: FileText,
    color: "blue",
    content: (
      <>
        <Step number="1" text="Buka menu 'Laporan' dari sidebar." />
        <Step number="2" text="Pilih bulan dan tahun untuk melihat rekap." />
        <Step number="3" text="Lihat skor kinerja per guru dan peringkat sekolah." />
        <Step number="4" text="Klik nama guru untuk laporan individu detail." />
        <Step number="5" text="Gunakan tombol 'Download PDF' atau 'Download Excel' untuk export." />
        <Tip>Laporan individu mencakup grafik harian, statistik kehadiran, dan penilaian (A-E).</Tip>
      </>
    )
  },
  {
    title: "Peta Absensi",
    icon: MapPin,
    color: "emerald",
    content: (
      <>
        <Step number="1" text="Buka menu 'Peta' dari sidebar." />
        <Step number="2" text="Peta menampilkan lokasi semua sekolah dan titik absensi guru hari ini." />
        <Step number="3" text="Lingkaran di setiap sekolah menunjukkan radius geofence." />
        <Step number="4" text="Pilih tanggal untuk melihat absensi di tanggal tertentu." />
        <Step number="5" text="Klik marker untuk detail absensi (waktu, status, foto)." />
      </>
    )
  },
  {
    title: "Audit Log",
    icon: Settings,
    color: "indigo",
    content: (
      <>
        <Step number="1" text="Buka menu 'Audit Log' dari sidebar." />
        <Step number="2" text="Semua aktivitas penting tercatat: login, absensi, perubahan data, dll." />
        <Step number="3" text="Gunakan filter untuk mencari log spesifik." />
        <Tip>Audit log bersifat immutable (tidak bisa diubah/dihapus) untuk menjaga integritas data.</Tip>
      </>
    )
  },
];

// ===== PDF GENERATION =====

const generatePDF = (user) => {
  const doc = new jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  let y = 20;

  const addTitle = (text) => {
    if (y > 260) { doc.addPage(); y = 20; }
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 30, 100);
    doc.text(text, 14, y);
    y += 8;
    doc.setDrawColor(79, 70, 229);
    doc.line(14, y, pageW - 14, y);
    y += 6;
  };

  const addStep = (num, text) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(79, 70, 229);
    doc.text(`${num}.`, 18, y);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(text, pageW - 40);
    doc.text(lines, 26, y);
    y += lines.length * 5 + 2;
  };

  const addTip = (text) => {
    if (y > 265) { doc.addPage(); y = 20; }
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(245, 158, 11);
    doc.roundedRect(14, y - 3, pageW - 28, 12, 2, 2, 'FD');
    doc.setFontSize(9);
    doc.setTextColor(146, 64, 14);
    doc.text(`Tips: ${text}`, 18, y + 4);
    y += 14;
  };

  const addText = (text) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(60, 60, 60);
    const lines = doc.splitTextToSize(text, pageW - 28);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 2;
  };

  // Header
  doc.setFillColor(30, 27, 75);
  doc.rect(0, 0, pageW, 40, 'F');
  doc.setFontSize(20);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(255, 255, 255);
  doc.text('SIGAP GURU', 14, 18);
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  doc.setTextColor(199, 210, 254);
  doc.text('Panduan Penggunaan Aplikasi', 14, 26);
  doc.setFontSize(9);
  doc.text('Dinas Pendidikan Kabupaten Asmat', 14, 33);
  doc.setTextColor(245, 158, 11);
  doc.text(`Dicetak: ${new Date().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageW - 14, 33, { align: 'right' });
  y = 52;

  // Guru Section (always shown)
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.setTextColor(30, 30, 100);
  doc.text('Panduan untuk Guru', 14, y);
  y += 10;

  addTitle('Cara Melakukan Absensi');
  addStep(1, 'Buka menu Absensi dari sidebar.');
  addStep(2, 'Pastikan GPS dan kamera aktif.');
  addStep(3, 'Ambil foto selfie. Pastikan wajah terlihat jelas.');
  addStep(4, 'Verifikasi wajah otomatis dilakukan (kedipkan mata).');
  addStep(5, "Tekan 'Absen Masuk' atau 'Absen Pulang'.");
  addStep(6, 'Foto diberi watermark otomatis (nama, waktu, koordinat, sekolah).');
  addTip('Jika GPS tidak akurat, pindah ke tempat terbuka.');

  addTitle('Mode Offline');
  addStep(1, 'Lakukan absensi meskipun tidak ada internet.');
  addStep(2, "Data disimpan di perangkat dan ditandai 'pending'.");
  addStep(3, 'Saat internet tersedia, data otomatis disinkronkan.');
  addTip('Verifikasi wajah tetap bisa dilakukan offline.');

  addTitle('Upload Foto Kegiatan');
  addStep(1, "Buka menu 'Foto Kegiatan'.");
  addStep(2, 'Anda harus sudah absen masuk hari ini.');
  addStep(3, 'Ambil foto atau pilih dari galeri, beri keterangan.');
  addStep(4, 'Maksimal 3 foto kegiatan per hari.');

  addTitle('Mengajukan Izin');
  addStep(1, "Buka menu 'Izin'.");
  addStep(2, "Klik 'Ajukan Izin'.");
  addStep(3, 'Isi tanggal mulai, selesai, dan alasan.');
  addStep(4, 'Izin menunggu persetujuan Kepala Sekolah/Admin.');

  addTitle('Mendaftarkan Wajah');
  addStep(1, "Buka menu 'Profil'.");
  addStep(2, "Klik 'Daftarkan Wajah'.");
  addStep(3, 'Posisikan wajah di bingkai kamera, kedipkan mata.');
  addTip('Wajah juga bisa didaftarkan oleh Admin/Kepala Sekolah.');

  // Kepsek Section
  if (user?.role !== 'guru') {
    doc.addPage();
    y = 20;
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 30, 100);
    doc.text('Panduan untuk Kepala Sekolah', 14, y);
    y += 10;

    addTitle('Monitoring Kehadiran Guru');
    addStep(1, 'Dashboard menampilkan ringkasan kehadiran guru hari ini.');
    addStep(2, 'Lihat siapa yang sudah dan belum absen.');
    addStep(3, "Buka 'Riwayat' untuk riwayat per tanggal.");
    addStep(4, "Buka 'Laporan' untuk rekap bulanan dan skor kinerja.");

    addTitle('Menyetujui/Menolak Izin');
    addStep(1, "Buka menu 'Izin'.");
    addStep(2, 'Daftar izin guru di sekolah Anda akan tampil.');
    addStep(3, "Klik 'Setujui' atau 'Tolak' pada izin pending.");

    addTitle('Mendaftarkan Wajah Guru');
    addStep(1, "Buka menu 'Daftar Wajah'.");
    addStep(2, 'Pilih guru yang belum punya data wajah.');
    addStep(3, 'Ambil foto wajah guru. Sistem menyimpan data biometrik.');

    addTitle('Mode Kiosk (Device Bersama)');
    addStep(1, "Buka menu 'Mode Kiosk'.");
    addStep(2, "Klik 'Daftarkan Device' dan beri nama.");
    addStep(3, 'Salin token device.');
    addStep(4, 'Buka halaman Kiosk di perangkat bersama, masukkan token.');
    addStep(5, 'Guru pilih nama, verifikasi wajah, dan absen.');
  }

  // Admin Section (only for non-guru)
  if (user?.role !== 'guru') {
    doc.addPage();
    y = 20;
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(30, 30, 100);
    doc.text('Panduan untuk Admin Dinas', 14, y);
    y += 10;

    addTitle('Kelola Sekolah');
    addStep(1, "Buka menu 'Sekolah'.");
    addStep(2, "Klik 'Tambah Sekolah'.");
    addStep(3, 'Isi nama, alamat, jenjang, koordinat, radius geofence.');
    addStep(4, "Atur jam operasional: 'Pagi Saja' atau 'Pagi & Siang (2 Shift)'.");
    addStep(5, 'Untuk 2 shift, atur jam siang dan toleransi terpisah.');
    addTip('Radius geofence menentukan area valid absensi. Default 100m.');

    addTitle('Manajemen User');
    addStep(1, "Buka menu 'Manajemen User'.");
    addStep(2, 'Tambah user baru dengan NIP, nama, role, sekolah.');
    addStep(3, 'Role: Admin Dinas, Pimpinan, Editor, Pengawas, Kepala Sekolah, Guru.');
    addStep(4, 'Password minimal 8 karakter (huruf besar, kecil, angka).');

    addTitle('Pengaturan Timezone & Hari Aktif');
    addStep(1, "Buka menu 'Pengaturan' untuk mengubah timezone (WIB/WITA/WIT).");
    addStep(2, "Buka menu 'Hari Aktif' untuk atur hari sekolah dan libur.");
    addTip('Timezone mempengaruhi perhitungan status kehadiran.');

    addTitle('Security Alerts & Monitoring');
    addStep(1, "Buka menu 'Security Alerts'.");
    addStep(2, 'Lihat alert: fake GPS, luar radius, anomali waktu.');
    addStep(3, "Review: 'Reviewed', 'Confirmed', atau 'Dismissed'.");
    addTip('Sistem otomatis deteksi GPS palsu dan anomali timestamp.');

    addTitle('Laporan & Export');
    addStep(1, "Buka menu 'Laporan'. Pilih bulan/tahun.");
    addStep(2, 'Lihat skor kinerja guru dan peringkat sekolah.');
    addStep(3, "Klik guru untuk laporan individu (grafik, statistik, penilaian A-E).");
    addStep(4, "Gunakan 'Download PDF' atau 'Download Excel' untuk export.");

    addTitle('Peta Absensi');
    addStep(1, "Buka menu 'Peta'.");
    addStep(2, 'Peta menampilkan lokasi sekolah dan titik absensi guru.');
    addStep(3, 'Lingkaran menunjukkan radius geofence.');
    addStep(4, 'Klik marker untuk detail absensi.');

    addTitle('Audit Log');
    addStep(1, "Buka menu 'Audit Log'.");
    addStep(2, 'Semua aktivitas penting tercatat dan tidak bisa diubah.');
  }

  // Footer on each page
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(`SIGAP GURU - Panduan Penggunaan | Hal ${i}/${totalPages}`, pageW / 2, 290, { align: 'center' });
  }

  doc.save('Panduan_SIGAP_GURU.pdf');
};


// ===== MAIN COMPONENT =====

const PanduanPage = () => {
  const { user } = useAuth();
  const isGuru = user?.role === 'guru';

  return (
    <div className="p-4 lg:p-8 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <BookOpen className="w-7 h-7 text-indigo-600" />
            Panduan Penggunaan
          </h1>
          <p className="text-gray-500 mt-1">Panduan lengkap fitur aplikasi SIGAP GURU</p>
        </div>
        <Button
          onClick={() => generatePDF(user)}
          className="bg-indigo-950 hover:bg-indigo-900"
          data-testid="download-pdf-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PDF
        </Button>
      </div>

      {/* Guru Guide */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          <Badge className="bg-indigo-100 text-indigo-800 text-sm px-3 py-1">Panduan Guru</Badge>
        </div>
        {guruGuide.map((item, idx) => (
          <Section key={idx} title={item.title} icon={item.icon} color={item.color} defaultOpen={item.defaultOpen}>
            {item.content}
          </Section>
        ))}
      </div>

      {/* Kepsek Guide - hidden from guru */}
      {!isGuru && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-emerald-100 text-emerald-800 text-sm px-3 py-1">Panduan Kepala Sekolah</Badge>
          </div>
          {kepsekGuide.map((item, idx) => (
            <Section key={idx} title={item.title} icon={item.icon} color={item.color} defaultOpen={item.defaultOpen}>
              {item.content}
            </Section>
          ))}
        </div>
      )}

      {/* Admin Guide - hidden from guru */}
      {!isGuru && (
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Badge className="bg-amber-100 text-amber-800 text-sm px-3 py-1">Panduan Admin Dinas</Badge>
          </div>
          {adminGuide.map((item, idx) => (
            <Section key={idx} title={item.title} icon={item.icon} color={item.color} defaultOpen={item.defaultOpen}>
              {item.content}
            </Section>
          ))}
        </div>
      )}
    </div>
  );
};

export default PanduanPage;
